import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { SEED_GALLERY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gallery() {
    const [images, setImages] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/gallery`)
            .then((res) => {
                if (!mounted) return;
                setImages(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => setImages([]))
            .finally(() => mounted && setLoaded(true));
        return () => {
            mounted = false;
        };
    }, []);

    const display = [
        ...images.map((img) => ({
            url: `${API}/files/${img.id}`,
            caption: img.caption || "",
            id: img.id,
        })),
        ...(images.length === 0 && loaded ? SEED_GALLERY : []),
    ];

    return (
        <section
            id="gallery"
            data-testid="gallery-section"
            className="bg-[#F7F5F0] py-24 md:py-32 border-t border-[#D1CFC9]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-14 md:mb-20">
                    <div className="col-span-12 md:col-span-6">
                        <p className="eyebrow">§ 05</p>
                        <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-3 leading-[0.92] text-[#1A1A1A]">
                            Onstage,
                            <br />
                            on screen,{" "}
                            <span className="italic">BTS.</span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-6 md:pl-12 flex items-end">
                        <p className="text-base md:text-lg text-[#4A4A4A] font-light leading-relaxed max-w-md">
                            Moments from favorite performances.
                        </p>
                    </div>
                </div>

                {display.length === 0 ? (
                    <p
                        data-testid="gallery-empty"
                        className="text-sm text-[#4A4A4A]"
                    >
                        Gallery loading…
                    </p>
                ) : (
                    <div
                        data-testid="gallery-grid"
                        className="columns-1 sm:columns-2 lg:columns-3 gap-3 md:gap-5 [column-fill:_balance]"
                    >
                        {display.map((img, idx) => (
                            <motion.figure
                                key={img.id || idx}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{
                                    duration: 0.6,
                                    delay: Math.min(idx * 0.04, 0.4),
                                }}
                                data-testid={`gallery-item-${idx}`}
                                className="relative mb-3 md:mb-5 break-inside-avoid overflow-hidden bg-[#EBE9E4] group"
                            >
                                <img
                                    src={img.url}
                                    alt={img.caption || `Production ${idx + 1}`}
                                    loading="lazy"
                                    className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                                />
                                {img.caption && (
                                    <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent text-[#F7F5F0] text-[10px] tracking-[0.18em] uppercase px-3 pt-8 pb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                        {img.caption}
                                    </figcaption>
                                )}
                            </motion.figure>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
